import { Type } from '@sinclair/typebox';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import styles from '../../../../LotteryForm.module.scss';
import { ControlledInputField } from '../../../../components/controlled-input-field/ControlledInputField';
import { useFormModule } from '../../../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer } from '../../../types';

export type SwipeNormalFormType = {
  maxNumberOfLives: string;
  numberOfCorrectAnswers: string;
};

export const SwipeNormalFormTypeSchema = Type.Object(
  {
    maxNumberOfLives: Type.Integer({
      minimum: 3,
      maximum: 5,
      errorMessage: {
        type: 'form-error-message-numeric-values',
        minimum: 'form-error-message-minimum-numeric-value',
        maximum: 'form-error-message-maximum-numeric-value',
      },
    }),
    numberOfCorrectAnswers: Type.Integer({
      minimum: 3,
      maximum: 20,
      errorMessage: {
        type: 'form-error-message-numeric-values',
        minimum: 'form-error-message-minimum-numeric-value',
        maximum: 'form-error-message-maximum-numeric-value',
      },
    }),
  },
  { additionalProperties: true, $id: 'SwipeNormalFormTypeSchema' }
);

export const SwipeRulesNormalFormTransformer: ModuleTransformer<SwipeNormalFormType> = {
  fromPayload: ({ lottery }) => {
    if (lottery.swipe?.config?.modeData.mode === 'NORMAL') {
      return {
        maxNumberOfLives: '' + (lottery.swipe?.config?.modeData.maxNumberOfLives ?? 0),
        numberOfCorrectAnswers: '' + (lottery.swipe?.config?.modeData.numberOfCorrectAnswers ?? 0),
      };
    } else {
      return {
        maxNumberOfLives: '',
        numberOfCorrectAnswers: '',
      };
    }
  },
  toPayload: (formData) => {
    return {
      lottery: {
        swipe: {
          config: {
            modeData: {
              maxNumberOfLives: parseInt(formData.maxNumberOfLives, 10),
              numberOfCorrectAnswers: parseInt(formData.numberOfCorrectAnswers, 10),
            },
          },
        },
      },
    };
  },
};

export type SwipeNormalFormProps = FormModuleProps<SwipeNormalFormType> & {
  moduleName: string;
  setRequiredNumberOfQuestions: (requiredNumberOfQuestions: number) => void;
};

export const SwipeNormalForm: React.FC<SwipeNormalFormProps> = ({
  moduleName,
  values,
  onChange,
  onValidation,
  setRequiredNumberOfQuestions,
}) => {
  const { control } = useFormModule({
    moduleName,
    defaultValues: values,
    schema: SwipeNormalFormTypeSchema,
    onChange,
    onValidation,
  });

  // Need to check dynamically: numberOfQuestions >= numberOfCorrectAnswers + maxNumberOfLives - 1;
  const maxNumberOfLives = useWatch({ control, name: 'maxNumberOfLives' });
  const numberOfCorrectAnswers = useWatch({
    control,
    name: 'numberOfCorrectAnswers',
  });

  useEffect(() => {
    const test = parseInt(numberOfCorrectAnswers, 10) + parseInt(maxNumberOfLives, 10) - 1;
    setRequiredNumberOfQuestions(test);
  }, [maxNumberOfLives, numberOfCorrectAnswers, setRequiredNumberOfQuestions]);

  return (
    <>
      <div className={styles['form-elements']}>
        <ControlledInputField
          control={control}
          labelKey="form-sign-up-swipe-config-max-number-of-lives-label"
          name="maxNumberOfLives"
          type="number"
          fieldHelper={{
            msg: `${SwipeNormalFormTypeSchema.properties.maxNumberOfLives.minimum}-${SwipeNormalFormTypeSchema.properties.maxNumberOfLives.maximum}`,
          }}
          showCharacterLimit={false}
          upperLimit={SwipeNormalFormTypeSchema.properties.maxNumberOfLives.maximum}
          lowerLimit={SwipeNormalFormTypeSchema.properties.maxNumberOfLives.minimum}
        />
      </div>
      <div className={styles['form-elements']}>
        <ControlledInputField
          control={control}
          labelKey="form-sign-up-swipe-config-number-of-correct-answers-label"
          name="numberOfCorrectAnswers"
          type="number"
          fieldHelper={{
            msg: `${SwipeNormalFormTypeSchema.properties.numberOfCorrectAnswers.minimum}-${SwipeNormalFormTypeSchema.properties.numberOfCorrectAnswers.maximum}`,
          }}
          showCharacterLimit={false}
          upperLimit={SwipeNormalFormTypeSchema.properties.numberOfCorrectAnswers.maximum}
          lowerLimit={SwipeNormalFormTypeSchema.properties.numberOfCorrectAnswers.minimum}
        />
      </div>
    </>
  );
};
